import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDistanceToNow } from "date-fns";
import { useTripAnalytics } from "@/api/trips";
import { Package, MapPin } from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

interface TripDetailsDialogProps {
  tripId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Create mock data for task completion over time
const generateTaskData = (
  completedTasks: number,
  failedTasks: number,
  pendingTasks: number
) => {
  const total = completedTasks + failedTasks + pendingTasks;
  if (total === 0) return [];

  // Create synthetic data for a realistic chart
  return [
    { name: "Start", completed: 0, failed: 0, pending: total },
    {
      name: "Progress 1",
      completed: Math.floor(completedTasks * 0.3),
      failed: Math.floor(failedTasks * 0.2),
      pending:
        total -
        Math.floor(completedTasks * 0.3) -
        Math.floor(failedTasks * 0.2),
    },
    {
      name: "Progress 2",
      completed: Math.floor(completedTasks * 0.7),
      failed: Math.floor(failedTasks * 0.6),
      pending:
        total -
        Math.floor(completedTasks * 0.7) -
        Math.floor(failedTasks * 0.6),
    },
    {
      name: "Current",
      completed: completedTasks,
      failed: failedTasks,
      pending: pendingTasks,
    },
  ];
};

// Create pie chart data for task statuses
const generatePieData = (
  completedTasks: number,
  failedTasks: number,
  pendingTasks: number
) => {
  return [
    { name: "Completed", value: completedTasks, color: "#22c55e" },
    { name: "Failed", value: failedTasks, color: "#f87171" },
    { name: "Pending", value: pendingTasks, color: "#60a5fa" },
  ].filter((item) => item.value > 0);
};

export function TripDetailsDialog({
  tripId,
  open,
  onOpenChange,
}: TripDetailsDialogProps) {
  const { data, isLoading } = useTripAnalytics(tripId);

  // Early return if dialog is closed or no tripId
  if (!open || !tripId) {
    return null;
  }

  // Helper function to format status badge
  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      PLANNED: "bg-blue-500",
      ACTIVE: "bg-yellow-500",
      COMPLETED: "bg-green-500",
      ARCHIVED: "bg-gray-500",
    };

    return (
      <Badge
        className={`${statusColors[status] || "bg-gray-500"}`}
        variant="default"
      >
        {status.replace("_", " ")}
      </Badge>
    );
  };

  // Generate chart data if analytics are available
  const taskChartData = data?.analytics?.taskCompletion
    ? generateTaskData(
        data.analytics.taskCompletion.completed,
        data.analytics.taskCompletion.failed,
        data.analytics.taskCompletion.pending
      )
    : [];

  const taskPieData = data?.analytics?.taskCompletion
    ? generatePieData(
        data.analytics.taskCompletion.completed,
        data.analytics.taskCompletion.failed,
        data.analytics.taskCompletion.pending
      )
    : [];

  // Helper function to format duration in hours and minutes
  const formatDuration = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes}m`;
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (remainingMinutes === 0) {
      return `${hours}h`;
    }

    return `${hours}h ${remainingMinutes}m`;
  };

  // Add a helper function to format order status badge colors consistently with orders-table.tsx
  const getOrderStatusColor = (status: string): string => {
    const statusColors: Record<string, string> = {
      REGISTERED: "bg-blue-500",
      PENDING: "bg-gray-500",
      PICKUP_PENDING: "bg-yellow-500",
      OUT_FOR_PICKUP: "bg-blue-500",
      PICKUP_ASSIGNED: "bg-blue-500",
      PICKED_UP: "bg-indigo-700",
      DEPOT_RECEIVE_PENDING: "bg-purple-500",
      DEPOT_RECEIVE_IN_PROGRESS: "bg-purple-500",
      AT_DEPOT: "bg-purple-500",
      DEPOT_SORT_PENDING: "bg-violet-500",
      DEPOT_SORT_IN_PROGRESS: "bg-violet-500",
      SORTED: "bg-violet-500",
      DEPOT_DISPATCH_PENDING: "bg-cyan-500",
      DEPOT_DISPATCH_IN_PROGRESS: "bg-cyan-500",
      IN_TRANSIT: "bg-cyan-500",
      DELIVERY_PENDING: "bg-yellow-500",
      OUT_FOR_DELIVERY: "bg-yellow-500",
      DELIVERED: "bg-green-700",
      RETURN_PENDING: "bg-orange-500",
      RETURN_IN_PROGRESS: "bg-orange-500",
      RETURNED: "bg-orange-500",
      PICKUP_FAILED: "bg-red-500",
      DEPOT_RECEIVE_FAILED: "bg-red-500",
      DEPOT_SORT_FAILED: "bg-red-500",
      DEPOT_DISPATCH_FAILED: "bg-red-500",
      DELIVERY_FAILED: "bg-red-500",
      RETURN_FAILED: "bg-red-500",
      FAILED: "bg-red-500",
      PICKUP_CANCELLED: "bg-red-700",
      DEPOT_RECEIVE_CANCELLED: "bg-red-700",
      DEPOT_SORT_CANCELLED: "bg-red-700",
      DEPOT_DISPATCH_CANCELLED: "bg-red-700",
      DELIVERY_CANCELLED: "bg-red-700",
      RETURN_CANCELLED: "bg-red-700",
      CANCELLED: "bg-red-700",
      PARTIALLY_PICKED_UP: "bg-indigo-500",
      PARTIALLY_DELIVERED: "bg-green-500",
      // Common simplified statuses for the UI
      Processing: "bg-yellow-500",
      Completed: "bg-green-700",
      Cancelled: "bg-red-700",
      Failed: "bg-red-500",
    };

    return statusColors[status] || "bg-gray-500";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[90vw] max-h-[90vh] overflow-y-auto bg-gray-50 p-0 border-0">
        <DialogHeader className="bg-white p-6 rounded-t-lg border-b">
          <DialogTitle className="flex items-center gap-2 text-xl">
            {isLoading ? (
              <Skeleton className="h-6 w-48" />
            ) : (
              <>
                <span className="font-semibold">
                  Trip #{data?.tripInfo?.tripNumber ?? tripId.slice(0, 8)}
                </span>
                {data?.tripInfo?.status && getStatusBadge(data.tripInfo.status)}
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {isLoading ? (
              <Skeleton className="h-4 w-64" />
            ) : (
              <>
                Created{" "}
                {data?.tripInfo?.createdAt &&
                  formatDistanceToNow(new Date(data.tripInfo.createdAt), {
                    addSuffix: true,
                  })}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-[100px] w-full" />
              ))}
            </div>
            <Skeleton className="h-[300px] w-full" />
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Metric Cards - First Row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-muted-foreground mb-1">
                      Orders
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-3xl font-semibold">
                        {data?.analytics?.totalOrders ?? 0}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground mt-1">
                      Total orders
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-muted-foreground mb-1">
                      Parcels
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-3xl font-semibold">
                        {data?.analytics?.totalParcels ?? 0}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground mt-1">
                      Total parcels
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-muted-foreground mb-1">
                      Duration
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-3xl font-semibold">
                        {data?.analytics?.totalDuration
                          ? formatDuration(data.analytics.totalDuration)
                          : "-"}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground mt-1">
                      Total duration
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Metric Cards - Second Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-muted-foreground mb-1">
                      Pickups
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-3xl font-semibold">
                        {data?.analytics?.totalPickupTasks ?? 0}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground mt-1">
                      Pickup tasks
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-muted-foreground mb-1">
                      Deliveries
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-3xl font-semibold">
                        {data?.analytics?.totalDeliveryTasks ?? 0}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground mt-1">
                      Delivery tasks
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Task Completion Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Task Completion</CardTitle>
                  <CardDescription>
                    Completion progress over time
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart
                        data={taskChartData}
                        margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                      >
                        <defs>
                          <linearGradient
                            id="colorCompleted"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="5%"
                              stopColor="#22c55e"
                              stopOpacity={0.8}
                            />
                            <stop
                              offset="95%"
                              stopColor="#22c55e"
                              stopOpacity={0.1}
                            />
                          </linearGradient>
                          <linearGradient
                            id="colorFailed"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="5%"
                              stopColor="#f87171"
                              stopOpacity={0.8}
                            />
                            <stop
                              offset="95%"
                              stopColor="#f87171"
                              stopOpacity={0.1}
                            />
                          </linearGradient>
                          <linearGradient
                            id="colorPending"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="5%"
                              stopColor="#60a5fa"
                              stopOpacity={0.8}
                            />
                            <stop
                              offset="95%"
                              stopColor="#60a5fa"
                              stopOpacity={0.1}
                            />
                          </linearGradient>
                        </defs>
                        <XAxis dataKey="name" />
                        <YAxis />
                        <CartesianGrid strokeDasharray="3 3" />
                        <Tooltip />
                        <Area
                          type="monotone"
                          dataKey="completed"
                          stackId="1"
                          stroke="#22c55e"
                          fillOpacity={1}
                          fill="url(#colorCompleted)"
                        />
                        <Area
                          type="monotone"
                          dataKey="failed"
                          stackId="1"
                          stroke="#f87171"
                          fillOpacity={1}
                          fill="url(#colorFailed)"
                        />
                        <Area
                          type="monotone"
                          dataKey="pending"
                          stackId="1"
                          stroke="#60a5fa"
                          fillOpacity={1}
                          fill="url(#colorPending)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Task Status Donut Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Task Status</CardTitle>
                  <CardDescription>Current task distribution</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px] flex items-center justify-center">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={taskPieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="value"
                          label={({
                            name,
                            percent,
                          }: {
                            name: string;
                            percent: number;
                          }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {taskPieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Order List */}
            <Card>
              <CardHeader>
                <CardTitle>Orders</CardTitle>
                <CardDescription>All orders in this trip</CardDescription>
              </CardHeader>
              <CardContent>
                {data?.orders && data.orders.length > 0 ? (
                  <div className="space-y-2">
                    {data.orders.map((order) => (
                      <div
                        key={order.id}
                        className="flex items-center justify-between p-4 rounded-lg border bg-card text-card-foreground"
                      >
                        <div className="flex items-center gap-4">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">
                                #{order.orderNumber || order.id.slice(0, 8)}
                              </span>
                              <Badge
                                className={getOrderStatusColor(
                                  order.status || "N/A"
                                )}
                              >
                                {order.status || "N/A"}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              {order.recipientName || "No recipient name"}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center text-sm text-muted-foreground">
                          {order.deliveryAddress?.line1 ? (
                            <div className="flex items-center">
                              <MapPin className="h-4 w-4 mr-1" />
                              <span className="truncate max-w-[200px]">
                                {order.deliveryAddress.line1}
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center text-muted-foreground/60">
                              <MapPin className="h-4 w-4 mr-1" />
                              <span>No address</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10 rounded-lg border border-dashed">
                    <Package className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                      No orders found
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
